"use client";

import { useResolveHref } from "@/lib/links";
import { AnimatePresence, motion, type Variants } from "framer-motion";
import { ArrowRight } from "@/components/ui/icons";
import type { Menu } from "./nav-data";

const EASE_OUT = [0.23, 1, 0.32, 1] as const;
const EASE_IN_OUT = [0.77, 0, 0.175, 1] as const;

const list: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.018, delayChildren: 0.08 } },
};
const item: Variants = {
  hidden: { opacity: 0, y: 8, filter: "blur(4px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.45, ease: EASE_OUT } },
};

type MegaMenuProps = {
  menu: Menu | null;
  onEnter: () => void;
  onLeave: () => void;
  onNavigate: () => void;
};

/**
 * Drops from beneath the bar by un-clipping downward (inset bottom 100% → 0), links
 * cascade in with a short stagger. Exit is faster than entry so the UI feels responsive.
 */
export function MegaMenu({ menu, onEnter, onLeave, onNavigate }: MegaMenuProps) {
  const r = useResolveHref();
  return (
    <AnimatePresence>
      {menu && (
        <motion.div
          key="mega"
          role="menu"
          aria-label={`${menu.label} menu`}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          initial={{ clipPath: "inset(0% 0% 100% 0% round 22px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 22px)" }}
          exit={{
            clipPath: "inset(0% 0% 100% 0% round 22px)",
            transition: { duration: 0.3, ease: EASE_IN_OUT },
          }}
          transition={{ duration: 0.55, ease: EASE_OUT }}
          className="absolute left-[var(--gutter)] top-[calc(var(--nav-h)-4px)] hidden w-[min(880px,calc(100vw-2*var(--gutter)))] overflow-hidden rounded-[22px] bg-white text-ink shadow-[0_1px_0_rgba(0,0,0,0.04),0_24px_60px_-20px_rgba(0,0,0,0.35)] ring-1 ring-black/[0.04] lg:block"
        >
          <motion.div variants={list} initial="hidden" animate="show" className="px-7 pb-7 pt-6">
            <motion.a
              variants={item}
              href={r(menu.headline.href)}
              onClick={onNavigate}
              className="group inline-flex items-center gap-2.5 text-[16px] font-medium tracking-[-0.02em]"
            >
              {menu.headline.label}
              <ArrowRight className="size-4 transition-transform duration-300 ease-[var(--ease-premium)] group-hover:translate-x-1" />
            </motion.a>

            <div className="mt-6 grid grid-cols-4 items-start gap-x-6">
              {menu.columns.map((column, ci) => (
                <div key={ci} className="space-y-6">
                  {column.map((g) => (
                    <div key={g.title}>
                      <motion.p variants={item} className="text-[14px] font-medium tracking-[-0.01em]">
                        {g.title}
                      </motion.p>
                      <ul className="mt-2.5 space-y-2">
                        {g.links.map((link) => (
                          <motion.li variants={item} key={link.label}>
                            <a
                              href={r(link.href)}
                              onClick={onNavigate}
                              role="menuitem"
                              className="inline-flex items-center gap-1.5 text-[14px] text-muted transition-colors duration-200 hover:text-ink"
                            >
                              {link.label}
                              {link.soon && (
                                <span className="rounded-full bg-panel px-1.5 py-px text-[9.5px] font-medium text-muted">Soon</span>
                              )}
                            </a>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
